import type { SemanticFaceMap } from '../semantic/semanticFaceMap';
import type { LipDetection } from '../tracking/faceLandmarker';
import { createCanvas, scaleMask } from './maskUtils';
import { stackBlur } from './stackBlur';

export interface ComplexionOptions {
  semanticMap?: SemanticFaceMap | null;
  veilOpacity?: number;
  underEyeOpacity?: number;
}

export interface ComplexionRenderResult {
  canvas: HTMLCanvasElement;
}

const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
  361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
  162, 21, 54, 103, 67, 109
];

const RIGHT_EYE = [
  33, 246, 161, 160, 159, 158, 157, 173, 133,
  155, 154, 153, 145, 144, 163, 7
];
const LEFT_EYE = [
  263, 466, 388, 387, 386, 385, 384, 398, 362,
  382, 381, 380, 374, 373, 390, 249
];
const OUTER_LIP = [
  61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291,
  375, 321, 405, 314, 17, 84, 181, 91, 146
];

const RIGHT_LOWER_LID = [33, 7, 163, 144, 145, 153, 154, 155, 133];
const LEFT_LOWER_LID = [263, 249, 390, 373, 374, 380, 381, 382, 362];

function tracePath(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  indices: readonly number[],
  width: number,
  height: number
): boolean {
  let started = false;
  for (const idx of indices) {
    const p = detection.landmarks[idx];
    if (!p) continue;
    const x = p.x * width;
    const y = p.y * height;
    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  if (started) ctx.closePath();
  return started;
}

function buildSkinMask(
  detection: LipDetection,
  width: number,
  height: number,
  semanticMap?: SemanticFaceMap | null
): HTMLCanvasElement {
  if (semanticMap) return scaleMask(semanticMap.masks.skin, width, height);

  const mask = createCanvas(width, height);
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx) return mask;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  tracePath(ctx, detection, FACE_OVAL, width, height);
  tracePath(ctx, detection, RIGHT_EYE, width, height);
  tracePath(ctx, detection, LEFT_EYE, width, height);
  tracePath(ctx, detection, OUTER_LIP, width, height);
  ctx.fill('evenodd');
  stackBlur(ctx, 0, 0, width, height, Math.max(4, Math.min(width, height) * 0.01));
  return mask;
}

function point(
  detection: LipDetection,
  idx: number,
  width: number,
  height: number
): { x: number; y: number } | null {
  const p = detection.landmarks[idx];
  return p ? { x: p.x * width, y: p.y * height } : null;
}

function drawUnderEye(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  lowerLid: readonly number[],
  outerIdx: number,
  innerIdx: number,
  width: number,
  height: number
): void {
  const outer = point(detection, outerIdx, width, height);
  const inner = point(detection, innerIdx, width, height);
  const lid = lowerLid
    .map((idx) => point(detection, idx, width, height))
    .filter((p): p is { x: number; y: number } => Boolean(p));
  if (!outer || !inner || lid.length < 3) return;

  const eyeWidth = Math.hypot(inner.x - outer.x, inner.y - outer.y);
  const topOffset = Math.max(2, eyeWidth * 0.035);
  const bottomOffset = Math.max(10, eyeWidth * 0.20);
  const bottom = lid.map((p, index) => {
    const t = lid.length <= 1 ? 0.5 : index / (lid.length - 1);
    const centerWeight = 1 - Math.abs(t - 0.5) * 1.25;
    return {
      x: p.x,
      y: p.y + bottomOffset * (0.72 + Math.max(0, centerWeight) * 0.42)
    };
  });
  const top = lid.map((p) => ({ x: p.x, y: p.y + topOffset }));

  ctx.moveTo(top[0]!.x, top[0]!.y);
  for (let i = 1; i < top.length; i++) ctx.lineTo(top[i]!.x, top[i]!.y);
  for (let i = bottom.length - 1; i >= 0; i--) ctx.lineTo(bottom[i]!.x, bottom[i]!.y);
  ctx.closePath();
}

function buildUnderEyeMask(
  detection: LipDetection,
  width: number,
  height: number,
  skinMask: HTMLCanvasElement
): HTMLCanvasElement {
  const mask = createCanvas(width, height);
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx) return mask;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  drawUnderEye(ctx, detection, RIGHT_LOWER_LID, 33, 133, width, height);
  drawUnderEye(ctx, detection, LEFT_LOWER_LID, 263, 362, width, height);
  ctx.fill();
  stackBlur(ctx, 0, 0, width, height, Math.max(4, Math.min(width, height) * 0.012));
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(skinMask, 0, 0, width, height);
  ctx.globalCompositeOperation = 'source-over';
  return mask;
}

function clampByte(value: number): number {
  return value < 0 ? 0 : value > 255 ? 255 : value;
}

function meanSkinColor(source: ImageData, mask: Uint8ClampedArray): [number, number, number] {
  const data = source.data;
  let r = 0;
  let g = 0;
  let b = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 16) {
    const alpha = (mask[i + 3] ?? mask[i] ?? 0) / 255;
    if (alpha < 0.18) continue;
    r += data[i] ?? 0;
    g += data[i + 1] ?? 0;
    b += data[i + 2] ?? 0;
    count++;
  }
  if (!count) return [218, 184, 166];
  return [r / count, g / count, b / count];
}

export function renderComplexionOverlay(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection,
  options: ComplexionOptions = {}
): ComplexionRenderResult {
  const width = detection.imageWidth;
  const height = detection.imageHeight;
  const veilOpacity = Math.max(0, Math.min(0.18, options.veilOpacity ?? 0.075));
  const underEyeOpacity = Math.max(0, Math.min(0.24, options.underEyeOpacity ?? 0.11));

  const out = createCanvas(width, height);
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('2D context unavailable');
  ctx.drawImage(source, 0, 0, width, height);
  if (veilOpacity <= 0 && underEyeOpacity <= 0) return { canvas: out };

  const skinMask = buildSkinMask(detection, width, height, options.semanticMap);
  const underEyeMask = buildUnderEyeMask(detection, width, height, skinMask);
  const sourceImg = ctx.getImageData(0, 0, width, height);
  const resultImg = ctx.getImageData(0, 0, width, height);
  const skinData = skinMask.getContext('2d', { willReadFrequently: true })?.getImageData(0, 0, width, height).data;
  const underEyeData = underEyeMask.getContext('2d', { willReadFrequently: true })?.getImageData(0, 0, width, height).data;
  if (!skinData || !underEyeData) return { canvas: out };

  const mean = meanSkinColor(sourceImg, skinData);
  const veilTarget: [number, number, number] = [
    mean[0] * 0.86 + 232 * 0.14,
    mean[1] * 0.88 + 214 * 0.12,
    mean[2] * 0.88 + 202 * 0.12
  ];
  const correctorTarget: [number, number, number] = [
    Math.min(255, veilTarget[0] + 14),
    Math.min(255, veilTarget[1] + 10),
    Math.min(255, veilTarget[2] + 4)
  ];

  const src = sourceImg.data;
  const dst = resultImg.data;
  for (let i = 0; i < dst.length; i += 4) {
    const skinAlpha = (skinData[i + 3] ?? skinData[i] ?? 0) / 255;
    if (skinAlpha <= 0.01) continue;
    const underAlpha = (underEyeData[i + 3] ?? underEyeData[i] ?? 0) / 255;
    const skinStrength = veilOpacity * skinAlpha;
    const correctorStrength = underEyeOpacity * underAlpha;
    for (let c = 0; c < 3; c++) {
      const original = src[i + c] ?? 0;
      // The complexion veil may brighten/soften, but should not darken the
      // brow bridge or face centre: darkening there reads as an accidental
      // contour line. Keep the correction strictly non-darkening per channel.
      const veilDelta = Math.max(0, veilTarget[c]! - original);
      const veiled = original + veilDelta * skinStrength;
      const correctorDelta = Math.max(0, correctorTarget[c]! - veiled);
      dst[i + c] = clampByte(veiled + correctorDelta * correctorStrength);
    }
    dst[i + 3] = 255;
  }

  ctx.putImageData(resultImg, 0, 0);
  return { canvas: out };
}
